/**
 * @fileoverview WebSocket client implementation for real-time order collaboration and communication.
 * Provides socket connection management, order status updates, and real-time messaging functionality.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import { io } from 'socket.io-client';

/**
 * SocketIO class for managing WebSocket connections and real-time communication
 * between users working on orders. Handles connection management, order updates,
 * and collaborative editing events.
 */
class SocketIO {
    /**
     * Creates a new SocketIO instance with the specified configuration options.
     *
     * @param {Object} options - Configuration options for the socket connection
     * @param {string} options.identity - User identity/email for connection identification
     * @param {string} options.nickName - Display name for the user
     * @constructor
     */
    constructor (options) {
        this.options = options;
        this.socket = null;

        // Define methods within the constructor function
        /**
         * Establishes a WebSocket connection to the specified URL
         *
         * @param {string} url - The WebSocket server URL to connect to
         * @returns {Promise<void>} Promise that resolves when connection is established
         */
        this.connect = function (url) {
            const self = this;
            self.url = url;
            const connectionOptions = {
                forceNew: true,
                reconnectionAttempts: 'Infinity', // avoid having user reconnect manually to prevent dead clients after a server restart
                timeout: 20000, // before connect_error and connect_timeout are emitted
                transports: ['websocket'],
            };

            return new Promise(function (resolve, reject) {
                self.socket = io(url, connectionOptions);
                self.socket.once('connect', resolve);
                self.socket.on('connect', function () {
                    self.socket.emit('info', { by: self.options.identity, nickName: self.options.nickName });
                });
                self.socket.once('error', reject);
                self.on = self.socket.on.bind(self.socket);
                self.once = self.socket.once.bind(self.socket);
                self.emit = self.socket.emit.bind(self.socket);
            });
        };

        /**
         * Private method for emitting socket events with promise-based response handling
         *
         * @private
         * @returns {Promise<any>} Promise that resolves with the server response
         */
        const emitAsync = function () {
            const self = this;
            const args = Array.prototype.slice.apply(arguments);
            return new Promise(function (resolve, reject) {
                self.socket.emit.apply(self.socket, args.concat([resolve]));
                self.socket.once('error', reject);
            });
        };

        /**
         * Opens an order for editing and notifies other users
         *
         * @param {string|number} orderNumber - The order number to open
         * @param {string} status - Current status of the order
         */
        this.open = function (orderNumber, status) {
            const self = this;
            self.socket.emit('open_order', {
                by: self.options.identity,
                nickName: self.options.nickName,
                orderNumber: orderNumber,
                status: status
            });
        };

        /**
         * Requests a refresh of the current data from the server
         */
        this.refresh = function () {
            const self = this;
            self.socket.emit('refresh');
        };

        /**
         * Updates an order status and broadcasts the change to other users
         *
         * @param {Object} request - The status update request
         * @param {string|number} request.orderNumber - Order number to update
         * @param {string} request.newStatus - New status value
         * @param {string} [request.by] - User making the change (automatically set)
         * @returns {Promise<any>} Promise that resolves with the server response
         */
        this.updateStatus = function (request) {
            const self = this;
            request.by = self.options.identity;
            return emitAsync.call(self, 'order_status_changing', request);
        };
    }
}

export default SocketIO;